import {
  DomainName,
  IFormInputControl,
  TControlName,
} from '@infotech-soe/metaschema';

import { TActionArgsValues } from '../../../../infrastructure/providers/abstract/IApiProvider';
import {
  ButtonFormControlViewModel,
  CheckboxFormControlViewModel,
  ColorInputFormControlViewModel,
  DatasetFormControlViewModel,
  DateFormControlViewModel,
  DictButtonFormControlViewModel,
  DictDateFormControlViewModel,
  DictionaryInputFormControlViewModel,
  DictionaryTreeFormControlViewModel,
  DictMultilineFormControlViewModel,
  FileInputFormControlViewModel,
  ImageInputFormControlViewModel,
  LocalizedProps,
  MonthYearFormControlViewModel,
  MultilineFormControlViewModel,
  NumericInputFormControlViewModel,
  RichTextFormControlViewModel,
  SelectFormControlViewModel,
  SetCheckboxGroupFormControlViewModel,
  SignatureButtonFormControlViewModel,
  TextInputFormControlViewModel,
  TimeInputFormControlViewModel,
} from '../../view-model/FormControlViewModel';
import { FormViewModel } from '../../view-model/FormViewModel';

import buildButtonControl from './buildButtonControl';
import buildCheckboxControl from './buildCheckboxControl';
import buildCheckGroupControl from './buildCheckGroupControl';
import buildColorInputControl from './buildColorInputControl';
import buildDatasetInputControl from './buildDatasetInputControl';
import buildDateControl from './buildDateControl';
import buildDictButtonControl from './buildDictButtonControl';
import buildDictDateControl from './buildDictDateControl';
import buildDictInputControl from './buildDictInputControl';
import buildDictMultilineControl from './buildDictMultilineControl';
import buildDictTreeControl from './buildDictTreeControl';
import buildFileInputControl from './buildFileInputControl';
import buildImageInputControl from './buildImageInputControl';
import buildMonthYearControl from './buildMonthYearControl';
import buildMultilineControl from './buildMultilineControl';
import buildNumericControl from './buildNumericControl';
import buildRichTextControl from './buildRichTextControl';
import BuildSelectControl from './buildSelectControl';
import buildSignatureButtonControl from './buildSignatureButtonControl';
import buildTextInputControl from './buildTextInputControl';
import buildTimeInputControl from './buildTimeInputControl';
import { controlDomain } from './helpers';

type DecoratedInputControl =
  | DictionaryTreeFormControlViewModel
  | CheckboxFormControlViewModel
  | ColorInputFormControlViewModel
  | SetCheckboxGroupFormControlViewModel
  | DictionaryInputFormControlViewModel
  | MultilineFormControlViewModel
  | DictMultilineFormControlViewModel
  | MonthYearFormControlViewModel
  | RichTextFormControlViewModel
  | DatasetFormControlViewModel
  | SelectFormControlViewModel
  | ButtonFormControlViewModel
  | DictButtonFormControlViewModel
  | SignatureButtonFormControlViewModel
  | DictDateFormControlViewModel
  | DateFormControlViewModel
  | FileInputFormControlViewModel
  | ImageInputFormControlViewModel
  | TextInputFormControlViewModel
  | NumericInputFormControlViewModel
  | TimeInputFormControlViewModel;

// TODO refactor all of this builders to make code DRY
const buildInputDecoratedControl = async (
  viewModel: FormViewModel,
  item: IFormInputControl,
  localizedProps: LocalizedProps,
  args: TActionArgsValues,
  domains: Array<{ name: TControlName; domain: DomainName }>
): Promise<DecoratedInputControl> => {
  switch (item.control) {
    case 'TreeNode':
      return buildDictTreeControl(
        viewModel,
        item,
        localizedProps,
        args,
        domains
      );

    case 'Checkbox':
      return buildCheckboxControl(item, localizedProps, args, viewModel.source);

    case 'ColorInput':
      return buildColorInputControl(
        item,
        localizedProps,
        args,
        viewModel.source
      );

    case 'CheckGroup':
      return buildCheckGroupControl(
        item,
        localizedProps,
        args,
        viewModel.source,
        domains
      );

    case 'DictInput':
      return buildDictInputControl(
        viewModel,
        item,
        localizedProps,
        args,
        domains
      );

    case 'MultilineTextInput':
      return buildMultilineControl(
        item,
        localizedProps,
        args,
        viewModel.source,
        domains
      );

    case 'DictMultilineTextInput':
      return buildDictMultilineControl(
        item,
        localizedProps,
        args,
        viewModel.source,
        domains
      );

    case 'MonthYear':
      return buildMonthYearControl(
        item,
        localizedProps,
        args,
        viewModel.source
      );

    case 'RichTextInput':
      return buildRichTextControl(
        item,
        localizedProps,
        args,
        viewModel.source,
        domains
      );

    case 'NumericInput':
      return buildNumericControl(
        item,
        localizedProps,
        args,
        viewModel.source,
        domains
      );

    case 'Select':
      return new BuildSelectControl().build(
        viewModel,
        item,
        localizedProps,
        args,
        domains
      );

    case 'Button':
      return buildButtonControl(item, localizedProps.label);

    case 'DictButton':
      return buildDictButtonControl(item, localizedProps.label, viewModel);

    case 'DateInput': {
      const domain = await controlDomain(item, viewModel.source, domains);

      return buildDateControl(
        domain,
        item,
        localizedProps,
        viewModel.source,
        args
      );
    }

    case 'DictDateInput': {
      const domain = await controlDomain(item, viewModel.source, domains);

      return buildDictDateControl(
        domain,
        item,
        localizedProps,
        viewModel.source,
        args
      );
    }

    case 'FileInput':
      return buildFileInputControl(viewModel, item, localizedProps, args);

    case 'ImageInput':
      return buildImageInputControl(item, localizedProps);

    case 'DatasetInput':
      return buildDatasetInputControl(item, localizedProps, viewModel);

    case 'TimeInput':
      return buildTimeInputControl(item, localizedProps, args, viewModel);

    case 'SignatureButton':
      return buildSignatureButtonControl(item, localizedProps.label);

    default:
      return buildTextInputControl(
        item,
        viewModel,
        localizedProps,
        args,
        domains
      );
  }
};

export default buildInputDecoratedControl;
